import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, CreditCard, FileText, Plus, Search, AlertTriangle, CheckCircle2, Clock, IndianRupee, Share2, MessageCircle, Mail, Loader2, Download, BarChart3, Users } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const classOptions = ['Nursery','LKG','UKG','Preparatory','1','2','3','4','5','6','7','8','9','10','11','12'];
const feeCategories = ['tuition', 'transport', 'lab', 'library', 'sports', 'exam', 'admission', 'other'];
const frequencies = ['monthly', 'quarterly', 'half-yearly', 'yearly', 'one-time'];
const paymentMethods = ['cash', 'online', 'cheque', 'dd', 'bank_transfer'];

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
}

export function FinancePage() {
  const { currentUser, isAdmin } = useApp();
  const isFinanceAdmin = currentUser?.role === 'finance_admin' || isAdmin;

  const [summary, setSummary] = useState(null);
  const [feeStructures, setFeeStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);

  // Form states
  const [feeForm, setFeeForm] = useState({ name: '', description: '', studentClass: '', amount: '', frequency: 'monthly', dueDay: 10, category: 'tuition', lateFeePercent: 0, lateFeeFixed: 0, concessionPercent: 0, installments: 1, genderApplicable: 'all' });
  const [invoiceForm, setInvoiceForm] = useState({ studentId: '', feeStructureId: '', amount: '', dueDate: '', description: '' });
  const [paymentForm, setPaymentForm] = useState({ invoiceId: '', amount: '', paymentMethod: 'cash', transactionId: '', remarks: '' });
  const [generateForm, setGenerateForm] = useState({ class: '', feeStructureId: '', dueDate: '' });

  // Reports & Defaulters
  const [defaulters, setDefaulters] = useState([]);
  const [headWise, setHeadWise] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [collSummary, setCollSummary] = useState(null);

  // Payroll
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ teacherId: '', basic: '', hra: '', da: '', ta: '', otherAllowances: '', pf: '', tax: '', otherDeductions: '' });
  const [payrollMonth, setPayrollMonth] = useState(new Date().toISOString().substring(0, 7));
  const [teachers, setTeachers] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => typeof window !== 'undefined' ? localStorage.getItem('sip_token') : '';

  // Search & filter
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [students, setStudents] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isShareLoading, setIsShareLoading] = useState(null);
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [emailingInvoice, setEmailingInvoice] = useState(null);
  const [collectionPeriod, setCollectionPeriod] = useState('monthly');
  const [collection, setCollection] = useState(null);

  useEffect(() => {
    loadData();
    loadRazorpayConfig();
  }, []);

  useEffect(() => { loadFeeCollection(); }, [collectionPeriod]);

  const loadFeeCollection = async () => {
    try {
      const data = await api.getFeeCollection(collectionPeriod);
      setCollection(data);
    } catch (e) { console.error(e); }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [summaryData, structuresData, invoicesData, paymentsData] = await Promise.all([
        api.getFinanceSummary().catch(() => null),
        api.listFeeStructures(),
        api.listInvoices(),
        api.listPayments(),
      ]);
      if (summaryData) setSummary(summaryData);
      setFeeStructures(structuresData);
      setInvoices(invoicesData);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Failed to load finance data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const data = await api.listStudents();
      setStudents(data);
    } catch (e) {
      console.error('Failed to load students:', e);
    }
  };

  const loadRazorpayConfig = async () => {
    try {
      const config = await api.getRazorpayConfig();
      setRazorpayConfig(config);
    } catch (e) {
      console.error('Failed to load Razorpay config:', e);
    }
  };

  const handlePayOnline = async (invoice) => {
    setPayingInvoice(invoice.invoiceId);
    try {
      const orderData = await api.createRazorpayOrder(invoice.invoiceId);

      if (orderData.mode === 'mock') {
        await api.verifyRazorpayPayment({
          razorpay_order_id: orderData.orderId,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          invoiceId: invoice.invoiceId,
        });
        toast.success('Mock payment recorded successfully');
        loadData();
        return;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Actanon Technologies',
        image: `${window.location.origin}/actanon-brand.png`,
        description: `${orderData.feeName} - ${orderData.studentName}`,
        order_id: orderData.orderId,
        prefill: {
          // In FinancePage the payer is typically the parent; fall back to
          // the school admin's own contact when parent data isn't present.
          name: orderData.parentName || orderData.studentName || currentUser?.name || '',
          email: orderData.parentEmail || currentUser?.email || '',
          contact: orderData.parentPhone || orderData.parentContactNumber || currentUser?.phone || '',
        },
        handler: async (response) => {
          try {
            await api.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              invoiceId: invoice.invoiceId,
            });
            toast.success('Payment successful!');
            loadData();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        theme: { color: '#1f3354' },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment gateway not loaded. Please refresh and try again.');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to initiate payment');
    } finally {
      setPayingInvoice(null);
    }
  };

  const handleEmailInvoice = async (invoiceId) => {
    setEmailingInvoice(invoiceId);
    try {
      const result = await api.sendInvoiceEmail(invoiceId);
      if (result.mode === 'mock') {
        toast.success('Invoice email logged (email service not configured yet)');
      } else {
        toast.success('Invoice email sent to parent');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send invoice email');
    } finally {
      setEmailingInvoice(null);
    }
  };

  // Create fee structure
  const handleCreateFee = async () => {
    if (!feeForm.name || !feeForm.studentClass || !feeForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    setIsSaving(true);
    try {
      await api.createFeeStructure({ ...feeForm, amount: parseFloat(feeForm.amount) });
      toast.success('Fee structure created');
      setIsFeeModalOpen(false);
      setFeeForm({ name: '', description: '', studentClass: '', amount: '', frequency: 'monthly', dueDay: 10, category: 'tuition', lateFeePercent: 0, lateFeeFixed: 0, concessionPercent: 0, installments: 1, genderApplicable: 'all' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create fee structure');
    } finally {
      setIsSaving(false);
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!invoiceForm.studentId || !invoiceForm.feeStructureId || !invoiceForm.amount || !invoiceForm.dueDate) {
      toast.error('Please fill required fields');
      return;
    }
    setIsSaving(true);
    try {
      await api.createInvoice({ ...invoiceForm, amount: parseFloat(invoiceForm.amount) });
      toast.success('Invoice created');
      setIsInvoiceModalOpen(false);
      setInvoiceForm({ studentId: '', feeStructureId: '', amount: '', dueDate: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create invoice');
    } finally {
      setIsSaving(false);
    }
  };

  // Record payment
  const handleRecordPayment = async () => {
    if (!paymentForm.invoiceId || !paymentForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    setIsSaving(true);
    try {
      await api.recordPayment({ ...paymentForm, amount: parseFloat(paymentForm.amount) });
      toast.success('Payment recorded');
      setIsPaymentModalOpen(false);
      setPaymentForm({ invoiceId: '', amount: '', paymentMethod: 'cash', transactionId: '', remarks: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setIsSaving(false);
    }
  };

  // Generate invoices for class
  const handleGenerateInvoices = async () => {
    if (!generateForm.class || !generateForm.feeStructureId || !generateForm.dueDate) {
      toast.error('Please fill required fields');
      return;
    }
    setIsSaving(true);
    try {
      const result = await api.generateInvoicesForClass(generateForm.class, generateForm.feeStructureId, generateForm.dueDate);
      toast.success(result.message);
      setIsGenerateModalOpen(false);
      setGenerateForm({ class: '', feeStructureId: '', dueDate: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to generate invoices');
    } finally {
      setIsSaving(false);
    }
  };

  // Share invoice with parent via WhatsApp
  const handleShareWithParent = async (invoiceId) => {
    setIsShareLoading(invoiceId);
    try {
      const info = await api.getInvoiceShareInfo(invoiceId);
      const whatsapp = info.whatsappNumber?.replace(/[^0-9]/g, '');
      if (!whatsapp) {
        toast.error(`No WhatsApp number found for ${info.studentName}. Update the student's WhatsApp number first.`);
        return;
      }

      const pending = info.pendingAmount;
      const message = encodeURIComponent(
        `Dear ${info.parentName || 'Parent'},\n\n` +
        `This is a fee reminder for ${info.studentName} (Class ${info.studentClass}).\n\n` +
        `Fee: ${info.feeName}\n` +
        `Total Amount: ₹${info.amount}\n` +
        `Paid: ₹${info.paidAmount}\n` +
        `Pending: ₹${pending}\n` +
        `Due Date: ${info.dueDate}\n\n` +
        `Please contact the school for online payment options.\n\n` +
        `Thank you.`
      );

      const phoneNumber = whatsapp.startsWith('91') ? whatsapp : `91${whatsapp}`;
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error(error.message || 'Failed to get share info');
    } finally {
      setIsShareLoading(null);
    }
  };

  // ---- New enterprise actions ----
  const applyLateFee = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/invoices/${invoiceId}/apply-late-fee`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const applyConcession = async (invoiceId) => {
    const pct = prompt('Enter concession percentage (1-100):');
    if (!pct || isNaN(pct)) return;
    const reason = prompt('Reason for concession:') || '';
    try {
      const res = await fetch(`${API_URL}/api/finance/invoices/${invoiceId}/concession`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ percent: parseFloat(pct), reason }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/payments/${paymentId}/receipt`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `receipt_${paymentId.substring(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch { toast.error('Failed to download receipt'); }
  };

  const updateChequeStatus = async (paymentId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/payments/${paymentId}/cheque-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ chequeStatus: status }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const loadReports = async () => {
    try {
      const [def, hw, os, cs] = await Promise.all([
        fetch(`${API_URL}/api/finance/defaulters`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/finance/reports/head-wise`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/finance/reports/outstanding`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/finance/reports/collection-summary`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
      ]);
      setDefaulters(def.defaulters || []);
      setHeadWise(hw.heads || []);
      setOutstanding(os);
      setCollSummary(cs);
    } catch { toast.error('Failed to load reports'); }
  };

  const loadPayroll = async () => {
    try {
      const [ss, ps, tl] = await Promise.all([
        fetch(`${API_URL}/api/payroll/salary-structures`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/payroll/payslips?month=${payrollMonth}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
        api.listStudents().catch(() => []),
      ]);
      setSalaryStructures(ss.structures || []);
      setPayslips(ps.payslips || []);
      // Load teachers for salary form
      try { const t = await api.listTeachers(); setTeachers(t || []); } catch {}
    } catch { toast.error('Failed to load payroll data'); }
  };

  const handleSaveSalary = async () => {
    if (!salaryForm.teacherId || !salaryForm.basic) { toast.error('Teacher and Basic salary required'); return; }
    setIsSaving(true);
    try {
      await fetch(`${API_URL}/api/payroll/salary-structure`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ...salaryForm, basic: parseFloat(salaryForm.basic) || 0, hra: parseFloat(salaryForm.hra) || 0, da: parseFloat(salaryForm.da) || 0, ta: parseFloat(salaryForm.ta) || 0, otherAllowances: parseFloat(salaryForm.otherAllowances) || 0, pf: parseFloat(salaryForm.pf) || 0, tax: parseFloat(salaryForm.tax) || 0, otherDeductions: parseFloat(salaryForm.otherDeductions) || 0 }),
      });
      toast.success('Salary structure saved');
      setIsSalaryModalOpen(false);
      setSalaryForm({ teacherId: '', basic: '', hra: '', da: '', ta: '', otherAllowances: '', pf: '', tax: '', otherDeductions: '' });
      loadPayroll();
    } catch { toast.error('Failed to save'); }
    setIsSaving(false);
  };

  const processPayroll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/process`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ month: payrollMonth }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadPayroll();
    } catch (e) { toast.error(e.message); }
  };

  const markPayslipPaid = async (payslipId) => {
    try {
      await fetch(`${API_URL}/api/payroll/payslips/${payslipId}/mark-paid`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ transactionRef: '' }) });
      toast.success('Marked as paid');
      loadPayroll();
    } catch { toast.error('Failed'); }
  };

  const downloadPayslip = async (payslipId, name, month) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/payslips/${payslipId}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `payslip_${name.replace(/ /g, '_')}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch { toast.error('Failed'); }
  };

  // Overdue check
  const today2 = new Date().toISOString().split('T')[0];
  const isOverdue = (inv) => inv.status !== 'paid' && inv.dueDate < today2;

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchSearch = !invoiceSearch || 
      inv.studentName?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.feeName?.toLowerCase().includes(invoiceSearch.toLowerCase());
    const matchStatus = invoiceStatusFilter === 'all' || inv.status === invoiceStatusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <Badge className="bg-emerald-500/10 text-emerald-600">Paid</Badge>;
      case 'partial': return <Badge className="bg-amber-500/10 text-amber-600">Partial</Badge>;
      case 'pending': return <Badge className="bg-red-500/10 text-red-600">Pending</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div data-testid="finance-page" className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="finance-page" className="space-y-6 animate-fade-in">
      <PageHeader
        title="Finance"
        description="Manage fees, invoices, and payments"
      >
        {isFinanceAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsFeeModalOpen(true)} data-testid="new-fee-structure-btn">
              <Plus className="w-4 h-4 mr-2" /> Fee Structure
            </Button>
            <Button
              className="bg-brand-deep-blue hover:bg-brand-deep-blue/90"
              onClick={() => { loadStudents(); setIsInvoiceModalOpen(true); }}
              data-testid="new-invoice-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> New Invoice
            </Button>
          </div>
        )}
      </PageHeader>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total Invoiced" value={formatCurrency(summary?.totalInvoiced || 0)} icon={FileText} delay={0} />
        <DataCard delay={0.05} className="relative overflow-visible">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Collected</p>
              <p className="text-3xl font-bold">
                {collection ? formatCurrency(collection.collected) : formatCurrency(summary?.totalCollected || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {collectionPeriod === 'monthly' ? 'This Month' : collectionPeriod === 'quarterly' ? 'This Quarter' : 'This Year'}
                {collection ? ` (${collection.paymentCount} payments)` : ''}
              </p>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-500/10">
              <IndianRupee className="w-5 h-5 text-emerald-500" />
            </div>
          </div>
          <div className="mt-2">
            <Select value={collectionPeriod} onValueChange={setCollectionPeriod}>
              <SelectTrigger className="h-7 text-xs w-[120px]" data-testid="finance-collection-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </DataCard>
        <StatsCard title="Pending" value={formatCurrency(summary?.totalPending || 0)} icon={Clock} delay={0.1} />
        <StatsCard
          title="Overdue"
          value={summary?.overdueCount || 0}
          description="invoices overdue"
          icon={AlertTriangle}
          delay={0.15}
        />
      </div>

      <Tabs defaultValue="fee-structures" className="space-y-4">
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="fee-structures">Fee Structures ({feeStructures.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
          <TabsTrigger value="reports" onClick={loadReports}>Reports</TabsTrigger>
          <TabsTrigger value="defaulters" onClick={loadReports}>Defaulters</TabsTrigger>
          <TabsTrigger value="payroll" onClick={loadPayroll}>Payroll</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <DataCard>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by student or fee name..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  className="pl-9"
                  data-testid="invoice-search"
                />
              </div>
              <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="invoice-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              {isFinanceAdmin && (
                <Button variant="outline" onClick={() => setIsGenerateModalOpen(true)} data-testid="generate-invoices-btn">
                  Bulk Generate
                </Button>
              )}
            </div>

            {filteredInvoices.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Student</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Fee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Paid</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Due Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        {isFinanceAdmin && <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {filteredInvoices.map((inv) => (
                        <tr key={inv.invoiceId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{inv.studentName}</p>
                            <p className="text-xs text-muted-foreground">Class {inv.studentClass}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{inv.feeName}</td>
                          <td className="px-4 py-3 text-sm font-medium">{formatCurrency(inv.amount)}</td>
                          <td className="px-4 py-3 text-sm text-emerald-600">{formatCurrency(inv.paidAmount)}</td>
                          <td className="px-4 py-3 text-sm">{inv.dueDate}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1">
                              {isOverdue(inv) && <Badge className="bg-red-600/10 text-red-600 text-[10px]">OVERDUE</Badge>}
                              {inv.concessionPercent > 0 && <Badge className="bg-purple-500/10 text-purple-600 text-[10px]">{inv.concessionPercent}% off</Badge>}
                              {inv.lateFeeApplied > 0 && <Badge className="bg-orange-500/10 text-orange-600 text-[10px]">+{formatCurrency(inv.lateFeeApplied)} late</Badge>}
                            </div>
                            {getStatusBadge(inv.status)}
                          </td>
                          {isFinanceAdmin && (
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                {inv.status !== 'paid' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePayOnline(inv)}
                                    disabled={payingInvoice === inv.invoiceId}
                                    data-testid={`pay-online-${inv.invoiceId}`}
                                  >
                                    {payingInvoice === inv.invoiceId ? (
                                      <div className="w-3.5 h-3.5 border-2 border-brand-deep-blue border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <CreditCard className="w-3.5 h-3.5" />
                                    )}
                                    <span className="ml-1.5">Pay Online</span>
                                  </Button>
                                )}
                                {inv.status !== 'paid' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setPaymentForm({
                                        invoiceId: inv.invoiceId,
                                        amount: String(inv.amount - inv.paidAmount),
                                        paymentMethod: 'cash',
                                        transactionId: '',
                                        remarks: '',
                                      });
                                      setIsPaymentModalOpen(true);
                                    }}
                                    data-testid={`pay-invoice-${inv.invoiceId}`}
                                  >
                                    Record Payment
                                  </Button>
                                )}
                                {inv.status !== 'paid' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                                    onClick={() => handleShareWithParent(inv.invoiceId)}
                                    disabled={isShareLoading === inv.invoiceId}
                                    data-testid={`share-invoice-${inv.invoiceId}`}
                                  >
                                    {isShareLoading === inv.invoiceId ? (
                                      <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <MessageCircle className="w-3.5 h-3.5" />
                                    )}
                                    <span className="ml-1.5">WhatsApp</span>
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                  onClick={() => handleEmailInvoice(inv.invoiceId)}
                                  disabled={emailingInvoice === inv.invoiceId}
                                  data-testid={`email-invoice-${inv.invoiceId}`}
                                >
                                  {emailingInvoice === inv.invoiceId ? (
                                    <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Mail className="w-3.5 h-3.5" />
                                  )}
                                  <span className="ml-1.5">Email</span>
                                </Button>
                                {isOverdue(inv) && (
                                  <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 text-xs" onClick={() => applyLateFee(inv.invoiceId)} data-testid={`late-fee-${inv.invoiceId}`}>
                                    Late Fee
                                  </Button>
                                )}
                                {inv.status !== 'paid' && !inv.concessionPercent && (
                                  <Button variant="outline" size="sm" className="text-purple-600 border-purple-200 text-xs" onClick={() => applyConcession(inv.invoiceId)} data-testid={`concession-${inv.invoiceId}`}>
                                    Concession
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No invoices found</p>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Fee Structures Tab */}
        <TabsContent value="fee-structures">
          <DataCard>
            {feeStructures.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {feeStructures.map((fs) => (
                  <div
                    key={fs.feeStructureId}
                    className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-brand-light-blue/50 transition-colors"
                    data-testid={`fee-structure-${fs.feeStructureId}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">{fs.name}</h4>
                      <Badge variant="outline" className="capitalize">{fs.category}</Badge>
                    </div>
                    <p className="text-2xl font-bold text-brand-deep-blue mb-1">{formatCurrency(fs.amount)}</p>
                    <p className="text-sm text-muted-foreground capitalize">Class {fs.studentClass} · {fs.frequency}</p>
                    {fs.description && <p className="text-xs text-muted-foreground mt-2">{fs.description}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {fs.lateFeePercent > 0 && <Badge variant="outline" className="text-[10px]">Late: {fs.lateFeePercent}%/mo</Badge>}
                      {fs.lateFeeFixed > 0 && <Badge variant="outline" className="text-[10px]">Late: +{formatCurrency(fs.lateFeeFixed)}/mo</Badge>}
                      {fs.installments > 1 && <Badge variant="outline" className="text-[10px]">{fs.installments} installments</Badge>}
                      {fs.concessionPercent > 0 && <Badge variant="outline" className="text-[10px]">{fs.concessionPercent}% concession</Badge>}
                      {fs.genderApplicable && fs.genderApplicable !== 'all' && <Badge variant="outline" className="text-[10px] capitalize">{fs.genderApplicable} only</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-4">No fee structures defined yet</p>
                {isFinanceAdmin && (
                  <Button onClick={() => setIsFeeModalOpen(true)}>Create First Fee Structure</Button>
                )}
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <DataCard>
            {payments.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Payment ID</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Remarks</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {payments.map((p) => (
                        <tr key={p.paymentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-mono">{p.paymentId.substring(0, 8)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-emerald-600">{formatCurrency(p.amount)}</td>
                          <td className="px-4 py-3 text-sm capitalize">{p.paymentMethod.replace('_', ' ')}</td>
                          <td className="px-4 py-3 text-sm">
                            {['cheque', 'dd'].includes(p.paymentMethod) ? (
                              <Badge className={p.chequeStatus === 'cleared' ? 'bg-emerald-500/10 text-emerald-600' : p.chequeStatus === 'bounced' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}>
                                {p.chequeStatus || 'pending'}
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-500/10 text-emerald-600">completed</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">{new Date(p.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{p.remarks || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => downloadReceipt(p.paymentId)} data-testid={`receipt-${p.paymentId}`}>
                                <Download className="w-3 h-3 mr-1" /> Receipt
                              </Button>
                              {['cheque', 'dd'].includes(p.paymentMethod) && p.chequeStatus !== 'cleared' && (
                                <>
                                  <Button variant="outline" size="sm" className="text-xs h-7 text-emerald-600" onClick={() => updateChequeStatus(p.paymentId, 'cleared')}>Clear</Button>
                                  {p.chequeStatus !== 'bounced' && <Button variant="outline" size="sm" className="text-xs h-7 text-red-600" onClick={() => updateChequeStatus(p.paymentId, 'bounced')}>Bounce</Button>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No payments recorded yet</p>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="space-y-4">
            {/* Head-wise Report */}
            <DataCard title="Head-wise Fee Report" data-testid="head-wise-report">
              {headWise.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold capitalize">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Invoices</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Invoiced</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Collected</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Pending</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {headWise.map((h, i) => (
                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-medium capitalize">{h.category}</td>
                          <td className="px-4 py-3 text-sm">{h.count}</td>
                          <td className="px-4 py-3 text-sm">{formatCurrency(h.invoiced)}</td>
                          <td className="px-4 py-3 text-sm text-emerald-600">{formatCurrency(h.collected)}</td>
                          <td className="px-4 py-3 text-sm text-red-600">{formatCurrency(h.pending)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-6 text-sm text-muted-foreground">No data. Click the Reports tab to load.</p>
              )}
            </DataCard>

            {/* Outstanding Report */}
            <DataCard title={`Outstanding Report — Total: ${formatCurrency(outstanding?.totalOutstanding || 0)}`}>
              {outstanding?.classes?.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Class</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Students</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Invoices</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {outstanding.classes.map((c, i) => (
                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-medium">Class {c.class}</td>
                          <td className="px-4 py-3 text-sm">{c.students}</td>
                          <td className="px-4 py-3 text-sm">{c.invoiceCount}</td>
                          <td className="px-4 py-3 text-sm font-medium text-red-600">{formatCurrency(c.totalOutstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-6 text-sm text-muted-foreground">No outstanding fees</p>
              )}
            </DataCard>

            {/* Collection Summary */}
            <DataCard title={`Collection Summary — Grand Total: ${formatCurrency(collSummary?.grandTotal || 0)}`}>
              {collSummary?.periods?.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Period</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Payments</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Total</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">By Method</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {collSummary.periods.slice(0, 12).map((p, i) => (
                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-medium">{p.period}</td>
                          <td className="px-4 py-3 text-sm">{p.count}</td>
                          <td className="px-4 py-3 text-sm font-medium text-emerald-600">{formatCurrency(p.total)}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {Object.entries(p.methods || {}).map(([m, v]) => `${m}: ${formatCurrency(v)}`).join(' · ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-6 text-sm text-muted-foreground">No collection data</p>
              )}
            </DataCard>
          </div>
        </TabsContent>

        {/* Defaulters Tab */}
        <TabsContent value="defaulters">
          <DataCard title={`Fee Defaulters (${defaulters.length})`} data-testid="defaulters-list">
            {defaulters.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Class</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Overdue Invoices</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Total Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {defaulters.map((d, i) => (
                      <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            <span className="text-sm font-medium">{d.studentName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{d.studentClass}</td>
                        <td className="px-4 py-3"><Badge className="bg-red-500/10 text-red-600">{d.overdueInvoices}</Badge></td>
                        <td className="px-4 py-3 text-sm font-bold text-red-600">{formatCurrency(d.totalDue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                <p className="text-muted-foreground">No defaulters! All fees are up to date.</p>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                <Input type="month" value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)} className="w-[180px]" data-testid="payroll-month" />
                <Button variant="outline" size="sm" onClick={loadPayroll} data-testid="payroll-refresh">Refresh</Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsSalaryModalOpen(true); if (!teachers.length) loadPayroll(); }} data-testid="add-salary-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Salary Structure
                </Button>
                <Button size="sm" className="bg-brand-deep-blue" onClick={processPayroll} data-testid="process-payroll-btn">
                  Process Payroll
                </Button>
              </div>
            </div>

            {/* Salary Structures */}
            {salaryStructures.length > 0 && (
              <DataCard title={`Salary Structures (${salaryStructures.length})`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {salaryStructures.map((s, i) => (
                    <div key={i} className="p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                      <p className="font-medium text-sm">{s.teacherName}</p>
                      <p className="text-xl font-bold text-brand-deep-blue">{formatCurrency(s.net)}</p>
                      <p className="text-[10px] text-muted-foreground">Gross: {formatCurrency(s.gross)} | Ded: {formatCurrency(s.deductions)}</p>
                    </div>
                  ))}
                </div>
              </DataCard>
            )}

            {/* Payslips */}
            <DataCard title={`Payslips — ${payrollMonth} (${payslips.length})`}>
              {payslips.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Gross</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Deductions</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Net Pay</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {payslips.map(ps => (
                        <tr key={ps.payslipId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-medium">{ps.teacherName}</td>
                          <td className="px-4 py-3 text-sm">{formatCurrency(ps.gross)}</td>
                          <td className="px-4 py-3 text-sm text-red-500">{formatCurrency(ps.deductions)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-emerald-600">{formatCurrency(ps.net)}</td>
                          <td className="px-4 py-3"><Badge className={ps.status === 'paid' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}>{ps.status}</Badge></td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => downloadPayslip(ps.payslipId, ps.teacherName, ps.month)} data-testid={`payslip-pdf-${ps.payslipId}`}>
                                <Download className="w-3 h-3 mr-1" /> PDF
                              </Button>
                              {ps.status !== 'paid' && (
                                <Button size="sm" className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => markPayslipPaid(ps.payslipId)} data-testid={`payslip-pay-${ps.payslipId}`}>
                                  Mark Paid
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-sm text-muted-foreground">No payslips for {payrollMonth}. Process payroll to generate.</p>
              )}
            </DataCard>
          </div>
        </TabsContent>
      </Tabs>

      {/* Salary Structure Modal */}
      <Dialog open={isSalaryModalOpen} onOpenChange={setIsSalaryModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Salary Structure</DialogTitle>
            <DialogDescription>Set salary components for a teacher</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Teacher *</Label>
              <Select value={salaryForm.teacherId} onValueChange={v => setSalaryForm({...salaryForm, teacherId: v})}>
                <SelectTrigger data-testid="salary-teacher"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                <SelectContent>{teachers.map(t => <SelectItem key={t.teacherId || t.userId} value={t.teacherId || t.userId}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Basic *</Label><Input type="number" value={salaryForm.basic} onChange={e => setSalaryForm({...salaryForm, basic: e.target.value})} data-testid="salary-basic" /></div>
              <div><Label>HRA</Label><Input type="number" value={salaryForm.hra} onChange={e => setSalaryForm({...salaryForm, hra: e.target.value})} /></div>
              <div><Label>DA</Label><Input type="number" value={salaryForm.da} onChange={e => setSalaryForm({...salaryForm, da: e.target.value})} /></div>
              <div><Label>TA</Label><Input type="number" value={salaryForm.ta} onChange={e => setSalaryForm({...salaryForm, ta: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>PF</Label><Input type="number" value={salaryForm.pf} onChange={e => setSalaryForm({...salaryForm, pf: e.target.value})} /></div>
              <div><Label>Tax</Label><Input type="number" value={salaryForm.tax} onChange={e => setSalaryForm({...salaryForm, tax: e.target.value})} /></div>
              <div><Label>Other Ded.</Label><Input type="number" value={salaryForm.otherDeductions} onChange={e => setSalaryForm({...salaryForm, otherDeductions: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setIsSalaryModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSalary} disabled={isSaving} className="bg-brand-deep-blue" data-testid="save-salary-btn">{isSaving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Fee Structure Modal */}
      <Dialog open={isFeeModalOpen} onOpenChange={setIsFeeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Fee Structure</DialogTitle>
            <DialogDescription>Define a new fee category for students</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Name *</Label>
              <Input value={feeForm.name} onChange={(e) => setFeeForm({ ...feeForm, name: e.target.value })} placeholder="e.g. Monthly Tuition Fee" data-testid="fee-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Class *</Label>
                <Select value={feeForm.studentClass} onValueChange={(v) => setFeeForm({ ...feeForm, studentClass: v })}>
                  <SelectTrigger data-testid="fee-class-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {classOptions.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (INR) *</Label>
                <Input type="number" value={feeForm.amount} onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })} placeholder="5000" data-testid="fee-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={feeForm.category} onValueChange={(v) => setFeeForm({ ...feeForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {feeCategories.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={feeForm.frequency} onValueChange={(v) => setFeeForm({ ...feeForm, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {frequencies.map(f => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={feeForm.description} onChange={(e) => setFeeForm({ ...feeForm, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Late Fee %</Label>
                <Input type="number" value={feeForm.lateFeePercent} onChange={(e) => setFeeForm({ ...feeForm, lateFeePercent: parseFloat(e.target.value) || 0 })} placeholder="0" data-testid="fee-late-pct" />
              </div>
              <div>
                <Label>Late Fee Fixed</Label>
                <Input type="number" value={feeForm.lateFeeFixed} onChange={(e) => setFeeForm({ ...feeForm, lateFeeFixed: parseFloat(e.target.value) || 0 })} placeholder="0" data-testid="fee-late-fixed" />
              </div>
              <div>
                <Label>Installments</Label>
                <Input type="number" min="1" max="12" value={feeForm.installments} onChange={(e) => setFeeForm({ ...feeForm, installments: parseInt(e.target.value) || 1 })} data-testid="fee-installments" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default Concession %</Label>
                <Input type="number" value={feeForm.concessionPercent} onChange={(e) => setFeeForm({ ...feeForm, concessionPercent: parseFloat(e.target.value) || 0 })} placeholder="0" data-testid="fee-concession" />
              </div>
              <div>
                <Label>Gender Applicable</Label>
                <Select value={feeForm.genderApplicable} onValueChange={(v) => setFeeForm({ ...feeForm, genderApplicable: v })}>
                  <SelectTrigger data-testid="fee-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    <SelectItem value="male">Male Only</SelectItem>
                    <SelectItem value="female">Female Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsFeeModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFee} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-fee-structure-btn">
              {isSaving ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog open={isInvoiceModalOpen} onOpenChange={setIsInvoiceModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Generate a fee invoice for a student</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Student *</Label>
              <Select value={invoiceForm.studentId} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, studentId: v })}>
                <SelectTrigger data-testid="invoice-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.studentId} value={s.studentId}>{s.fullName} (Class {s.studentClass})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fee Structure *</Label>
              <Select value={invoiceForm.feeStructureId} onValueChange={(v) => {
                const fs = feeStructures.find(f => f.feeStructureId === v);
                setInvoiceForm({ ...invoiceForm, feeStructureId: v, amount: fs ? String(fs.amount) : invoiceForm.amount });
              }}>
                <SelectTrigger data-testid="invoice-fee-select"><SelectValue placeholder="Select fee" /></SelectTrigger>
                <SelectContent>
                  {feeStructures.map(fs => <SelectItem key={fs.feeStructureId} value={fs.feeStructureId}>{fs.name} - {formatCurrency(fs.amount)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (INR) *</Label>
                <Input type="number" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} data-testid="invoice-amount-input" />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input type="date" value={invoiceForm.dueDate} onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} data-testid="invoice-due-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsInvoiceModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-invoice-btn">
              {isSaving ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record a payment against an invoice</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (INR) *</Label>
                <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="payment-amount-input" />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.paymentMethod} onValueChange={(v) => setPaymentForm({ ...paymentForm, paymentMethod: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Transaction ID</Label>
              <Input value={paymentForm.transactionId} onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={paymentForm.remarks} onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-payment-btn">
              {isSaving ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Invoices Modal */}
      <Dialog open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Generate Invoices</DialogTitle>
            <DialogDescription>Create invoices for all students in a class</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Class *</Label>
              <Select value={generateForm.class} onValueChange={(v) => setGenerateForm({ ...generateForm, class: v })}>
                <SelectTrigger data-testid="generate-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  {classOptions.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fee Structure *</Label>
              <Select value={generateForm.feeStructureId} onValueChange={(v) => setGenerateForm({ ...generateForm, feeStructureId: v })}>
                <SelectTrigger data-testid="generate-fee-select"><SelectValue placeholder="Select fee" /></SelectTrigger>
                <SelectContent>
                  {feeStructures.map(fs => <SelectItem key={fs.feeStructureId} value={fs.feeStructureId}>{fs.name} - {formatCurrency(fs.amount)} (Class {fs.studentClass})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input type="date" value={generateForm.dueDate} onChange={(e) => setGenerateForm({ ...generateForm, dueDate: e.target.value })} data-testid="generate-due-date" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsGenerateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerateInvoices} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="generate-invoices-btn-confirm">
              {isSaving ? 'Generating...' : 'Generate Invoices'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
